/**
 * BONQUERÉ — recent searches.  BNQ-WEB-0261 · Sprint 3.4.5
 *
 * The approved Search HiFi opens the overlay with the customer's recent terms
 * (Design_Reference/10_Search/Search_HiFi.dc.html — `#overlay`, "Recent").
 *
 * WHY THIS IS CLIENT-SIDE
 * Shopify exposes no recent-search resource: the Predictive Search API returns matches for a
 * term, and the `search` object describes the current request only. Search history is therefore
 * the customer's own data and lives in `localStorage`, the same place Horizon keeps recently
 * viewed products (`assets/recently-viewed-products.js`). Nothing is sent anywhere.
 *
 * SCOPE — deliberately small. This file owns the history list and nothing else. The row markup
 * and the clock glyph come from a <template> rendered by `snippets/bnq-search-aside.liquid`, so
 * the icon has a single source in Liquid rather than being re-drawn here.
 *
 * WHEN A TERM IS RECORDED — on commitment, never on keystroke:
 *   • arriving at the search results page with a `q` (covers Enter, "View all results", a
 *     popular-search pill, and any link into search from anywhere in the storefront), and
 *   • following a prediction straight to a product or collection, which is a committed search
 *     that never loads a results page.
 */

const STORAGE_KEY = 'bnq:recent-searches';
const MAX_TERMS = 5;
const MIN_TERM_LENGTH = 2;

/**
 * Read the stored history, tolerating absent, corrupt or blocked storage.
 * @returns {string[]}
 */
function readTerms() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((term) => typeof term === 'string' && term.trim()).slice(0, MAX_TERMS);
  } catch {
    return [];
  }
}

/**
 * Persist the history. Storage can be unavailable (private mode, quota, blocked cookies);
 * that degrades the feature to "no history", never to an error.
 * @param {string[]} terms
 */
function writeTerms(terms) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(terms.slice(0, MAX_TERMS)));
  } catch {
    /* history is a convenience, not a requirement */
  }
}

/** Re-render every mounted list (there is normally exactly one). */
function refreshAll() {
  for (const element of document.querySelectorAll('bnq-recent-searches')) {
    if (element instanceof RecentSearches) element.render();
  }
}

/**
 * Record a committed search term, most recent first, de-duplicated case-insensitively.
 * @param {string | null | undefined} term
 */
function recordTerm(term) {
  const trimmed = (term ?? '').trim();
  if (trimmed.length < MIN_TERM_LENGTH) return;

  const next = readTerms().filter((existing) => existing.toLowerCase() !== trimmed.toLowerCase());
  next.unshift(trimmed);
  writeTerms(next);
  refreshAll();
}

/**
 * Remove one term.
 * @param {string} term
 */
function forgetTerm(term) {
  writeTerms(readTerms().filter((existing) => existing !== term));
  refreshAll();
}

function forgetAll() {
  writeTerms([]);
  refreshAll();
}

class RecentSearches extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  get #searchUrl() {
    return this.dataset.searchUrl || '/search';
  }

  render() {
    const template = this.querySelector('template');
    const terms = readTerms();

    // Keep the <template> — it is the row's only markup source — and replace everything else.
    for (const child of Array.from(this.children)) {
      if (child !== template) child.remove();
    }

    if (!template || terms.length === 0) {
      this.hidden = true;
      return;
    }

    const heading = document.createElement('h4');
    heading.className = 'bnq-search-aside__label';
    heading.append(this.dataset.heading || 'Recent');

    const clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'button-unstyled bnq-search-recent__clear';
    clear.dataset.recentClear = '';
    clear.append(this.dataset.clearLabel || 'Clear');
    heading.append(clear);

    const list = document.createElement('ul');
    list.className = 'bnq-search-recent__list list-unstyled';

    for (const term of terms) {
      const item = document.createElement('li');
      item.className = 'bnq-search-recent__item';
      item.append(template.content.cloneNode(true));

      const link = item.querySelector('[data-recent-link]');
      if (link instanceof HTMLAnchorElement) {
        link.href = `${this.#searchUrl}?q=${encodeURIComponent(term)}`;
      }

      const label = item.querySelector('[data-recent-term]');
      if (label) label.textContent = term;

      const remove = item.querySelector('[data-recent-remove]');
      if (remove instanceof HTMLElement) {
        remove.dataset.term = term;
        remove.setAttribute('aria-label', (this.dataset.removeLabel || 'Remove %s').replace('%s', term));
      }

      list.append(item);
    }

    this.append(heading, list);
    this.hidden = false;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-recent-clear]')) {
      event.preventDefault();
      forgetAll();
      return;
    }

    const remove = target.closest('[data-recent-remove]');
    if (remove instanceof HTMLElement && remove.dataset.term) {
      event.preventDefault();
      forgetTerm(remove.dataset.term);
    }
  };
}

if (!customElements.get('bnq-recent-searches')) {
  customElements.define('bnq-recent-searches', RecentSearches);
}

/* --- Recording -------------------------------------------------------------------------- */

const searchRoute = window.Theme?.routes?.search_url || '/search';

// 1 · Any arrival at the results page with a query — whatever route the customer took to get there.
if (window.location.pathname.startsWith(searchRoute)) {
  recordTerm(new URLSearchParams(window.location.search).get('q'));
}

// 2 · Following a prediction out of the overlay: the search was committed, but no results page
//     will ever load to observe it. Captured in the capture phase so it still runs when the
//     handler that navigates calls `preventDefault`.
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('.predictive-search-dropdown a[href]')) return;

    const input = target.closest('predictive-search-component')?.querySelector('input[name="q"]');
    if (input instanceof HTMLInputElement) recordTerm(input.value);
  },
  { capture: true }
);
